import { payloadKeyHash } from "@/lib/safe-query/key-hash"
import { stableStringify } from "@/lib/safe-query/stable-stringify"
import type { MenuItemInterface } from "@/models/menu-item"
import type { CartLine } from "@/store/cart/cart.types"

/**
 * Live fingerprint for optimistic pricing: cart + coupon + catalog prices + address + channel
 * + optional scheduling / UX context (must stay in sync with preview payload inputs).
 * Debounced before becoming the {@link pricingPreviewQueryKey} input.
 */
export function buildPricingLiveFingerprint(input: {
  cartLines: CartLine[]
  discountId: string
  menuItems: MenuItemInterface[]
  addressFingerprint: string
  orderType: number
  orderChannel: number
  /** e.g. delivery vs pickup when both exist; keep stable when unused. */
  deliveryModeFingerprint?: string
  /** Scheduled window / slot id when the API supports it. */
  scheduledSlotFingerprint?: string
}): string {
  const cart = stableStringify(
    input.cartLines.map((l) => ({
      id: l.client_item_id,
      q: l.quantity,
      m: l.menu_item_id,
      mods: l.selected_modifiers,
      c: l.selectedCombos,
      p: l.item_price,
    }))
  )
  const menuFp = stableStringify(
    (input.menuItems ?? []).map((m) => ({
      id: m.menu_item_id,
      p: m.item_price,
    }))
  )
  return stableStringify({
    cart,
    discountId: input.discountId,
    menuFp,
    address: input.addressFingerprint,
    orderType: input.orderType,
    orderChannel: input.orderChannel,
    deliveryMode: input.deliveryModeFingerprint ?? "",
    slot: input.scheduledSlotFingerprint ?? "",
  })
}

/**
 * Safe-query / cache key: `pricing-preview` namespace + restaurant + user + content hash.
 */
export function pricingPreviewQueryKey(restaurantId: string, userName: string, liveFingerprint: string): string {
  const u = userName.trim()
  if (!u || !liveFingerprint) return ""
  const h = payloadKeyHash(liveFingerprint)
  return `pricing-preview|v1|rid:${restaurantId}|u${encodeURIComponent(u)}|h${h}`
}
